import React from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { ChevronDown } from 'react-feather'
import { useReactiveVar } from '@apollo/client'
import {
  DemandFragment,
  PerformanceTableFragment,
  HourSeriesCollectionFragment,
  TemperatureCurveFragment,
  BuildingPartCollectionFragment,
} from '../../../../generated/graphql'
import { CommonOptions } from './hybrid-heat-pump/CommonOptions'
import { HeatingOptions } from './hybrid-heat-pump/HeatingOptions'
import { CoolingOptions } from './hybrid-heat-pump/CoolingOptions'
import { HeatCollectionOptions } from './hybrid-heat-pump/HeatCollectionOptions'
import { HeatExhaustOptions } from './hybrid-heat-pump/HeatExhaustOptions'
import { InternalFormValues } from '../form-values'
import { createHourSeriesNameFormatter } from '../../../../hour-series/name-formatter'
import { projectVar } from '../../../../apollo/reactive-vars'

const useStyles = makeStyles((theme) => ({
  accordion: {
    background: theme.palette.grey[200],
  },
}))

type Props = {
  hourSeriesCollections: HourSeriesCollectionFragment[]
  demands: DemandFragment[]
  temperatureCurves: TemperatureCurveFragment[]
  performanceTables: PerformanceTableFragment[]
  buildingParts: BuildingPartCollectionFragment[]
}

export function HybridHeatPumpForm({
  hourSeriesCollections,
  demands,
  temperatureCurves,
  performanceTables,
  buildingParts,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { control } = useFormContext<InternalFormValues>()
  const classes = useStyles()
  const project = useReactiveVar(projectVar)

  const heatSourceAvailable = useWatch<boolean>({
    control,
    name: 'parameters.hybridHeatPump.heatSourceAvailable',
  })
  const heatExhaustAvailable = useWatch<boolean>({
    control,
    name: 'parameters.hybridHeatPump.heatExhaustAvailable',
  })
  const heatRecycleAvailable = useWatch<boolean>({
    control,
    name: 'parameters.hybridHeatPump.heatRecycleAvailable',
  })

  if (!project) {
    return <></>
  }

  const hourSeriesNameFormatter = createHourSeriesNameFormatter(
    project,
    buildingParts,
  )

  return (
    <>
      <CommonOptions performanceTables={performanceTables} />

      <Box>
        {(heatSourceAvailable || heatRecycleAvailable) && (
          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="h6">
                {t('createProvider.forms.hybridHeatPump.heating')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <HeatingOptions
                demands={demands}
                temperatureCurves={temperatureCurves}
                hourSeriesNameFormatter={hourSeriesNameFormatter}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {(heatExhaustAvailable || heatRecycleAvailable) && (
          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="h6">
                {t('createProvider.forms.hybridHeatPump.cooling')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CoolingOptions temperatureCurves={temperatureCurves} />
            </AccordionDetails>
          </Accordion>
        )}

        {heatSourceAvailable && (
          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="h6">
                {t('createProvider.forms.hybridHeatPump.heatCollection')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <HeatCollectionOptions
                hourSeriesCollections={hourSeriesCollections}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {heatExhaustAvailable && (
          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="h6">
                {t('createProvider.forms.hybridHeatPump.heatExhaust')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <HeatExhaustOptions />
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </>
  )
}
